import { React, useEffect, useState } from "react";
import PageTitle from "../../../PageTitle";
import useAxios from "../../../../utilities/useAxios";
import {
	Table,
	Input,
	Select,
	Button,
	Space,
	Card,
	message,
	Modal,
	Layout,
	Form,
} from "antd";
import {
	DownloadOutlined,
	PlusOutlined,
	EditOutlined,
	DeleteOutlined,
} from "@ant-design/icons";
import XLSX from "xlsx-js-style";
import AccessedCarNavigation from "../AccessedCarNavigation";
import PageHeader from "../../../common/PageHeader";

const { Content } = Layout;

const ViewAccessedCars = () => {
	const [accessedCarsData, setAccessedCarsData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchText, setSearchText] = useState("");
	const [searchCategory, setSearchCategory] = useState("car_num");
	const [deleteModalVisible, setDeleteModalVisible] = useState(false);
	const [addModalVisible, setAddModalVisible] = useState(false);
	const [addSubmitting, setAddSubmitting] = useState(false);
	const [selectedCar, setSelectedCar] = useState(null);
	const [addForm] = Form.useForm();

	useEffect(() => {
		getAccessedCarsData();
		// Expose refresh function to window object
		window.refreshAccessedCars = getAccessedCarsData;
		// Cleanup function
		return () => {
			delete window.refreshAccessedCars;
		};
	}, []);

	const getAccessedCarsData = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/utils/accessed-car/",
			);
			if (response.status === 200) {
				setAccessedCarsData(response.data.accessed_car);
			}
		} catch (error) {
			message.error("차량 목록을 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (value) => {
		setSearchText(value);
	};

	const handleCategoryChange = (value) => {
		setSearchCategory(value);
	};

	const filteredData = accessedCarsData.filter((car) => {
		if (!searchText) return true;
		const searchValue =
			car[searchCategory]?.toString().toLowerCase() || "";
		return searchValue.includes(searchText.toLowerCase());
	});

	const handleEdit = (id) => {
		window.open(
			`/utils/accessed-car/edit-accessed-car/${id}/`,
			"popup",
		);
	};

	const handleDelete = (car) => {
		setSelectedCar(car);
		setDeleteModalVisible(true);
	};

	const handleAddCar = async (values) => {
		try {
			setAddSubmitting(true);
			const response = await useAxios.post(
				"/utils/accessed-car/",
				{
					car_num: values.car_num,
					owner: values.owner,
					owner_tel: values.owner_tel,
					inst: values.inst,
					inst_detail: values.inst_detail || null,
				},
			);
			if (response.status === 201) {
				message.success(
					"차량이 성공적으로 등록되었습니다.",
				);
				setAddModalVisible(false);
				addForm.resetFields();
				getAccessedCarsData();
			}
		} catch (error) {
			message.error(
				error.response?.data?.message ||
					"차량 등록 중 오류가 발생했습니다.",
			);
		} finally {
			setAddSubmitting(false);
		}
	};

	const confirmDelete = async () => {
		try {
			const response = await useAxios.delete(
				`/utils/accessed-car/${selectedCar.id}/`,
			);
			if (response.status === 200) {
				message.success("차량이 삭제되었습니다.");
				getAccessedCarsData();
			}
		} catch (error) {
			message.error("차량 삭제 중 오류가 발생했습니다.");
		} finally {
			setDeleteModalVisible(false);
			setSelectedCar(null);
		}
	};

	const downloadExcel = async () => {
		try {
			const date = new Date();
			const today = date.toISOString().split("T")[0];

			const wb = XLSX.utils.book_new();
			const headerStyle = {
				font: {
					bold: true,
					color: { rgb: "000000" },
					sz: 13,
				},
				fill: { fgColor: { rgb: "FAF4DB" } },
				alignment: {
					horizontal: "center",
					vertical: "center",
				},
				border: {
					left: {
						style: "thin",
						color: { auto: 1 },
					},
					right: {
						style: "thin",
						color: { auto: 1 },
					},
					top: {
						style: "thin",
						color: { auto: 1 },
					},
					bottom: {
						style: "thin",
						color: { auto: 1 },
					},
				},
			};

			const dataStyle = {
				font: { color: { rgb: "000000" }, sz: 11 },
				fill: { fgColor: { rgb: "FFFFFF" } },
				alignment: {
					horizontal: "center",
					vertical: "center",
				},
				border: {
					left: {
						style: "thin",
						color: { auto: 1 },
					},
					right: {
						style: "thin",
						color: { auto: 1 },
					},
					top: {
						style: "thin",
						color: { auto: 1 },
					},
					bottom: {
						style: "thin",
						color: { auto: 1 },
					},
				},
			};

			const headerRow = [
				{ v: "차량번호", t: "s", s: headerStyle },
				{ v: "차주", t: "s", s: headerStyle },
				{ v: "차주 연락처", t: "s", s: headerStyle },
				{ v: "소속", t: "s", s: headerStyle },
				{ v: "세부소속", t: "s", s: headerStyle },
				{ v: "차량등록일", t: "s", s: headerStyle },
				{ v: "수정일", t: "s", s: headerStyle },
			];

			const dataRows = accessedCarsData.map((car) => [
				{ v: car.car_num, t: "s", s: dataStyle },
				{ v: car.owner, t: "s", s: dataStyle },
				{
					v: car.owner_tel || "",
					t: "s",
					s: dataStyle,
				},
				{ v: car.inst || "", t: "s", s: dataStyle },
				{
					v: car.inst_detail || "",
					t: "s",
					s: dataStyle,
				},
				{
					v: car.regisration_date,
					t: "s",
					s: dataStyle,
				},
				{ v: car.edited_date, t: "s", s: dataStyle },
			]);

			const ws = XLSX.utils.aoa_to_sheet([
				headerRow,
				...dataRows,
			]);
			XLSX.utils.book_append_sheet(wb, ws, "차량 목록");
			XLSX.writeFile(wb, `차량 등록_${today}.xlsx`);
		} catch (error) {
			message.error("엑셀 다운로드 중 오류가 발생했습니다.");
		}
	};

	const columns = [
		{
			title: "차량번호",
			dataIndex: "car_num",
			key: "car_num",
			sorter: (a, b) => a.car_num.localeCompare(b.car_num),
		},
		{
			title: "차주",
			dataIndex: "owner",
			key: "owner",
			sorter: (a, b) => a.owner.localeCompare(b.owner),
		},
		{
			title: "차주 연락처",
			dataIndex: "owner_tel",
			key: "owner_tel",
		},
		{
			title: "소속",
			dataIndex: "inst",
			key: "inst",
			sorter: (a, b) =>
				(a.inst || "").localeCompare(b.inst || ""),
		},
		{
			title: "세부소속",
			dataIndex: "inst_detail",
			key: "inst_detail",
		},
		{
			title: "차량등록일",
			dataIndex: "regisration_date",
			key: "regisration_date",
			sorter: (a, b) =>
				new Date(a.regisration_date) -
				new Date(b.regisration_date),
		},
		{
			title: "수정일",
			dataIndex: "edited_date",
			key: "edited_date",
			sorter: (a, b) =>
				new Date(a.edited_date) -
				new Date(b.edited_date),
		},
		{
			title: "관리",
			key: "action",
			render: (_, record) => (
				<Space size="middle">
					<Button
						type="primary"
						icon={<EditOutlined />}
						onClick={() =>
							handleEdit(record.id)
						}>
						수정
					</Button>
					<Button
						danger
						icon={<DeleteOutlined />}
						onClick={() =>
							handleDelete(record)
						}>
						삭제
					</Button>
				</Space>
			),
		},
	];

	return (
		<>
			<PageTitle title="교인차량 등록 및 조회" />
			<Layout className="member-layout">
				<AccessedCarNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="utils-accessed_car__container page">
							<PageHeader
								title="교인 차량 등록 및 조회"
								actions={
									<>
										<div className="utils-accessed_car__header__toolbar">
									<Space
										wrap
										size={[
											8,
											8,
										]}>
										<Select
											defaultValue="car_num"
											style={{
												width: 120,
											}}
											onChange={
												handleCategoryChange
											}
											options={[
												{
													value: "car_num",
													label: "차량번호",
													defaultSelected: true,
												},
												{
													value: "owner",
													label: "차주",
												},
												{
													value: "inst",
													label: "소속",
												},
												{
													value: "inst_detail",
													label: "세부소속",
												},
												{
													value: "owner_tel",
													label: "차주 연락처",
												},
											]}
										/>
										<Input.Search
											placeholder="검색어를 입력하세요"
											allowClear
											onSearch={
												handleSearch
											}
											className="utils-accessed_car__search"
										/>
										<Button
											type="primary"
											icon={
												<DownloadOutlined />
											}
											onClick={
												downloadExcel
											}>
											엑셀
											다운로드
										</Button>
										<Button
											type="primary"
											icon={
												<PlusOutlined />
											}
											onClick={() => {
												addForm.resetFields();
												setAddModalVisible(
													true,
												);
											}}>
											차량
											추가
										</Button>
									</Space>
								</div>
									</>
								}
							/>
							<div className="utils-accessed_car__content-container">
								<Card className="surface-card utils-accessed_car__table-card">
									<Table
										columns={
											columns
										}
										dataSource={
											filteredData
										}
										rowKey="id"
										loading={
											loading
										}
										scroll={{
											x: 1100,
										}}
										tableLayout="fixed"
										sticky={{
											offsetHeader: 64,
										}}
										pagination={{
											pageSize: 10,
										}}
									/>
								</Card>
							</div>
						</div>

						<Modal
							title="신규 차량 등록"
							open={addModalVisible}
							onCancel={() => {
								setAddModalVisible(false);
								addForm.resetFields();
							}}
							footer={null}
							destroyOnClose
							width={520}>
							<Form
								form={addForm}
								layout="vertical"
								onFinish={handleAddCar}
								autoComplete="off">
								<Form.Item
									label="차주"
									name="owner"
									rules={[
										{
											required: true,
											message: "차주를 입력해주세요",
										},
									]}>
									<Input placeholder="차주 이름을 입력하세요" />
								</Form.Item>
								<Form.Item
									label="소속"
									name="inst"
									rules={[
										{
											required: true,
											message: "소속을 입력해주세요",
										},
									]}>
									<Input placeholder="소속을 입력하세요" />
								</Form.Item>
								<Form.Item
									label="세부소속"
									name="inst_detail">
									<Input placeholder="세부소속을 입력하세요" />
								</Form.Item>
								<Form.Item
									label="차량번호"
									name="car_num"
									rules={[
										{
											required: true,
											message: "차량번호를 입력해주세요",
										},
									]}>
									<Input placeholder="차량번호를 입력하세요" />
								</Form.Item>
								<Form.Item
									label="차주 연락처"
									name="owner_tel"
									rules={[
										{
											required: true,
											message: "연락처를 입력해주세요",
										},
										{
											pattern: /^[0-9-]+$/,
											message:
												"숫자와 하이픈(-)만 입력 가능합니다",
										},
									]}>
									<Input placeholder="연락처를 입력하세요" />
								</Form.Item>
								<Form.Item>
									<Space>
										<Button
											type="primary"
											htmlType="submit"
											loading={
												addSubmitting
											}>
											추가
										</Button>
										<Button
											onClick={() => {
												setAddModalVisible(
													false,
												);
												addForm.resetFields();
											}}>
											취소
										</Button>
									</Space>
								</Form.Item>
							</Form>
						</Modal>

						<Modal
							title="차량 삭제"
							open={
								deleteModalVisible
							}
							onOk={confirmDelete}
							onCancel={() => {
								setDeleteModalVisible(
									false,
								);
								setSelectedCar(
									null,
								);
							}}
							okText="삭제"
							cancelText="취소"
							okButtonProps={{
								danger: true,
							}}>
							<p>
								정말로
								삭제하시겠습니까?
							</p>
							{selectedCar && (
								<p>
									차량번호:{" "}
									{
										selectedCar.car_num
									}
									<br />
									차주:{" "}
									{
										selectedCar.owner
									}
								</p>
							)}
						</Modal>
					</Content>
				</Layout>
			</Layout>
		</>
	);
};

export default ViewAccessedCars;
